<template>
  <div class="candlestick-chart-container mx-auto" :style="containerStyle">
    <div class="chart-header">
      <h4 class="chart-title">{{ symbol }}</h4>
      <span class="chart-subtitle">{{ subtitle }}</span>
    </div>
    <div ref="chartContainer" class="chart-svg-container"></div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { useTheme } from 'vuetify'
import * as d3 from 'd3'
import type { CandlestickChartProps, ProcessedCandleData, ChartDimensions } from '@/types/chart'
import { processCandleData, getSymbolFromMetaData, formatPrice, formatVolume } from '@/utils/chart'

@Options({
  props: {
    chartData: { type: Object as () => CandlestickChartProps['data'], required: true },
    width: { type: Number, default: 800 },
    height: { type: Number, default: 400 },
    showVolume: { type: Boolean, default: true },
    showTooltip: { type: Boolean, default: true },
    enableZoom: { type: Boolean, default: true }
  },
  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => this.renderChart())
      },
      deep: true
    },
    isDark: {
      handler() {
        this.$nextTick(() => {
          this.renderChart()
        })
      }
    },
    width: {
      handler(newWidth: number, oldWidth: number) {
        if (newWidth !== oldWidth && this.isVisible) {
          this.rerenderWithDelay()
        }
      }
    },
    height: {
      handler(newHeight: number, oldHeight: number) {
        if (newHeight !== oldHeight && this.isVisible) {
          this.rerenderWithDelay()
        }
      }
    }
  }
})
export default class CandlestickChart extends Vue {
  // Props
  chartData!: CandlestickChartProps['data']
  width!: number
  height!: number
  showVolume!: boolean
  showTooltip!: boolean
  enableZoom!: boolean

  // Internal state
  theme = useTheme()
  isVisible = false
  intersectionObserver: IntersectionObserver | null = null
  resizeObserver: ResizeObserver | null = null
  
  // D3 selections and scales
  svg!: d3.Selection<SVGSVGElement, unknown, null, undefined>
  mainGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  contentGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  clipPath!: d3.Selection<SVGRectElement, unknown, null, undefined>
  xScale!: d3.ScaleTime<number, number>
  yScale!: d3.ScaleLinear<number, number>
  volumeScale!: d3.ScaleLinear<number, number>
  tooltip!: d3.Selection<HTMLDivElement, unknown, HTMLElement, any>
  currentTransform: d3.ZoomTransform = d3.zoomIdentity
  actualInnerWidth = 0
  actualInnerHeight = 0
  xAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  yAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  gridLinesGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  candlesGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  volumeGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  volumeAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  crosshairGroup!: d3.Selection<SVGGElement, unknown, null, undefined>
  overlayRect!: d3.Selection<SVGRectElement, unknown, null, undefined>
  tooltipId!: string
  isChartInitialized = false
  isZoomInitialized = false
  
  // Constants
  readonly volumeHeight = 60

  // Computed properties
  get symbol(): string {
    return getSymbolFromMetaData(this.chartData.meta_data)
  }

  get subtitle(): string {
    const meta = this.chartData.meta_data
    const interval = 'interval' in meta ? meta.interval : 'Daily'
    const lastRefreshed = new Date(meta.last_refreshed).toLocaleString()
    return `${interval} - ${lastRefreshed}`
  }

  get containerStyle() {
    return {
      width: '100%',
      maxWidth: `${this.width}px`
    }
  }

  get dimensions(): ChartDimensions {
    return {
      width: this.width,
      height: this.height,
      margin: { top: 20, right: 20, bottom: this.showVolume ? 20 : 20, left: 60 }
    }
  }

  get isDark(): boolean {
    return this.theme?.current?.value?.dark ?? false
  }

  get colors() {
    // Helper to convert hex to rgba, with a fallback for safety
    const hexToRgba = (hex: string, alpha: number) => {
      if (typeof hex !== 'string' || hex.length < 4) {
        return `rgba(128, 128, 128, ${alpha})` // Default gray if hex is invalid
      }
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    // Defensively check if the theme object is fully available
    if (this.theme?.current?.value?.colors) {
      const themeColors = this.theme.current.value.colors
      const onSurface = themeColors['on-surface']
      
      return {
        background: themeColors['surface-variant'],
        textColor: onSurface,
        gridLines: hexToRgba(onSurface, 0.2),
        upColor: themeColors.success,
        downColor: themeColors.error,
        crosshair: hexToRgba(onSurface, 0.5)
      }
    }

    // Fallback to default colors if the theme is not yet ready
    return {
      background: this.isDark ? '#1e1e1e' : '#ffffff',
      textColor: this.isDark ? '#ffffff' : '#000000',
      gridLines: this.isDark ? '#333333' : '#e0e0e0',
      upColor: '#26a69a',
      downColor: '#ef5350',
      crosshair: this.isDark ? '#999999' : '#666666'
    }
  }

  // Lifecycle hooks
  mounted() {
    this.$nextTick(() => {
      this.setupVisibilityDetection()
      
      setTimeout(() => {
        const chartContainer = this.$refs.chartContainer as HTMLElement
        if (chartContainer) {
          const rect = chartContainer.getBoundingClientRect()
          if (rect.width > 0) {
            this.renderChart()
          }
        }
        this.setupResizeObserver()
      }, 150)
    })
  }

  beforeUnmount() {
    // Clean up tooltip for this specific instance
    if (this.tooltipId) {
      d3.select(`#${this.tooltipId}`).remove()
    }
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.intersectionObserver) this.intersectionObserver.disconnect()
  }

  // Methods
  setupVisibilityDetection() {
    const chartContainer = this.$refs.chartContainer as HTMLElement
    if (!chartContainer) return
    
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isVisible) {
            this.isVisible = true
            setTimeout(() => {
              this.renderChart()
            }, 100)
          }
        })
      },
      { threshold: 0.1 }
    )
    
    this.intersectionObserver.observe(chartContainer)
  }

  setupResizeObserver() {
    const chartContainer = this.$refs.chartContainer as HTMLElement
    if (chartContainer) {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (entries[0].contentRect.width > 0) {
          this.rerenderWithDelay()
        }
      })
      this.resizeObserver.observe(chartContainer)
    }
  }

  rerenderWithDelay() {
    setTimeout(() => {
      if (this.$refs.chartContainer) {
        this.renderChart()
      }
    }, 100)
  }

  initChartStructure() {
    // Only initialize if not already done
    if (this.isChartInitialized) return
    
    // Generate unique IDs for this instance
    this.tooltipId = `candlestick-tooltip-${Math.random().toString(36).substr(2, 9)}`
    const clipPathId = `clip-${Math.random().toString(36).substr(2, 9)}`
    
    // Clear container
    const chartContainer = this.$refs.chartContainer as HTMLElement
    d3.select(chartContainer).selectAll('*').remove()
    
    // Create SVG and main group (one time only)
    this.svg = d3.select(chartContainer).append('svg')
    this.mainGroup = this.svg.append('g')
    
    // Create clip path
    this.clipPath = this.svg.append('defs')
      .append('clipPath')
      .attr('id', clipPathId)
      .append('rect')
    
    // Create content group
    this.contentGroup = this.mainGroup.append('g')
      .attr('class', 'chart-content')
      .attr('clip-path', `url(#${clipPathId})`)
    
    // Create persistent groups for each element type
    this.gridLinesGroup = this.contentGroup.append('g').attr('class', 'grid-lines')
    this.candlesGroup = this.contentGroup.append('g').attr('class', 'candles-group')
    this.volumeGroup = this.contentGroup.append('g').attr('class', 'volume-group')
    this.xAxisGroup = this.mainGroup.append('g').attr('class', 'x-axis')
    this.yAxisGroup = this.mainGroup.append('g').attr('class', 'y-axis')
    
    // Create scales (will be updated with data)
    this.xScale = d3.scaleTime()
    this.yScale = d3.scaleLinear()
    if (this.showVolume) {
      this.volumeScale = d3.scaleLinear()
      this.volumeAxisGroup = this.mainGroup.append('g').attr('class', 'volume-axis')
    }
    
    // Create tooltip with unique ID
    if (this.showTooltip) {
      // Remove any existing tooltip for this instance
      d3.select(`#${this.tooltipId}`).remove()
      
      this.tooltip = d3.select('body').append('div')
        .attr('id', this.tooltipId)
        .attr('class', 'candlestick-tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('pointer-events', 'none')
    }
    
    // Create crosshair once
    if (this.showTooltip) {
      this.crosshairGroup = this.contentGroup.append('g')
        .attr('class', 'crosshair')
        .style('display', 'none')
      
      this.crosshairGroup.append('line').attr('class', 'crosshair-x')
      this.crosshairGroup.append('line').attr('class', 'crosshair-y')
      
      // Create overlay rectangle for mouse events ONCE
      this.overlayRect = this.contentGroup.append('rect')
        .attr('class', 'overlay-rect')
        .style('fill', 'none')
        .style('pointer-events', 'all')
    }
    
    this.isChartInitialized = true
    this.isZoomInitialized = false
    this.currentTransform = d3.zoomIdentity
  }

  updateChartDimensions() {
    const chartContainer = this.$refs.chartContainer as HTMLElement
    if (!chartContainer) return
    
    const rect = chartContainer.getBoundingClientRect()
    let actualWidth = rect.width || this.width
    actualWidth = Math.max(300, actualWidth)
    
    const chartDimensions = { ...this.dimensions, width: actualWidth }
    this.actualInnerWidth = chartDimensions.width - this.dimensions.margin.left - this.dimensions.margin.right
    this.actualInnerHeight = this.dimensions.height - this.dimensions.margin.top - this.dimensions.margin.bottom
    
    if (!this.isChartInitialized) return
    
    // Update SVG dimensions
    this.svg
      .attr('width', chartDimensions.width)
      .attr('height', this.dimensions.height)
    
    this.mainGroup
      .attr('transform', `translate(${this.dimensions.margin.left},${this.dimensions.margin.top})`)
    
    // Update clip path
    this.clipPath
      .attr('width', this.actualInnerWidth)
      .attr('height', this.actualInnerHeight)
    
    // Update scale ranges
    this.xScale.range([0, this.actualInnerWidth])
    this.yScale.range([this.actualInnerHeight - (this.showVolume ? this.volumeHeight + 20 : 0), 0])
    if (this.showVolume) {
      this.volumeScale.range([this.volumeHeight, 0])
    }
    
    // Update axis positions
    this.xAxisGroup
      .attr('transform', `translate(0,${this.actualInnerHeight - (this.showVolume ? this.volumeHeight + 20 : 0)})`)
    
    this.volumeGroup
      .attr('transform', `translate(0,${this.actualInnerHeight - this.volumeHeight})`)
    
    if (this.volumeAxisGroup) {
      this.volumeAxisGroup
        .attr('transform', `translate(0,${this.actualInnerHeight - this.volumeHeight})`)
    }
    
    // Update overlay rectangle dimensions
    if (this.overlayRect) {
      this.overlayRect
        .attr('width', this.actualInnerWidth)
        .attr('height', this.actualInnerHeight - (this.showVolume ? this.volumeHeight + 20 : 0))
    }
    
    // Update crosshair line lengths
    if (this.crosshairGroup) {
      this.crosshairGroup.select('.crosshair-x')
        .attr('y2', this.actualInnerHeight - (this.showVolume ? this.volumeHeight + 20 : 0))
      this.crosshairGroup.select('.crosshair-y')
        .attr('x2', this.actualInnerWidth)
    }
    
    // Update tooltip styles
    if (this.tooltip) {
      this.tooltip
        .style('background', this.colors.background)
        .style('border', `1px solid ${this.colors.gridLines}`)
        .style('color', this.colors.textColor)
    }
  }

  renderChart() {
    if (!this.chartData.time_series.length) return
    
    // Initialize structure if needed
    this.initChartStructure()
    this.updateChartDimensions()
    
    const processedData = processCandleData(this.chartData.time_series).sort((a, b) => a.date.getTime() - b.date.getTime())
    this.updateScales(processedData)
    
    // Update each component using D3's update pattern
    this.updateAxes()
    this.updateGridLines()
    this.updateCandlesticks(processedData)
    
    if (this.showVolume) {
      this.updateVolume(processedData)
    }
    
    if (this.enableZoom) {
      this.setupZoom() // Only sets up zoom if not already done
    }
    
    if (this.showTooltip) {
      this.setupTooltipInteraction(processedData)
    }
  }

  updateScales(data: ProcessedCandleData[]) {
    this.xScale.domain(d3.extent(data, d => d.date) as [Date, Date])
    
    const yMin = d3.min(data, d => d.low)!
    const yMax = d3.max(data, d => d.high)!
    const padding = (yMax - yMin) * 0.1
    this.yScale.domain([yMin - padding, yMax + padding])
    
    if (this.showVolume) {
      this.volumeScale.domain([0, d3.max(data, d => d.volume)!])
    }
  }

  updateAxes() {
    // Update X axis
    this.xAxisGroup
      .call(d3.axisBottom(this.xScale)
        .tickFormat(d => {
          const date = d as Date
          if (date.getHours() === 0 && date.getMinutes() === 0) return d3.timeFormat('%b %d')(date)
          return d3.timeFormat('%H:%M')(date)
        }) as any)
    
    this.xAxisGroup.selectAll('line').style('stroke', this.colors.gridLines)
    this.xAxisGroup.select('.domain').style('stroke', this.colors.gridLines)
    
    // Update Y axis
    this.yAxisGroup
      .call(d3.axisLeft(this.yScale).tickFormat(d => formatPrice(d as number)) as any)
    
    this.yAxisGroup.selectAll('line').style('stroke', this.colors.gridLines)
    this.yAxisGroup.select('.domain').style('stroke', this.colors.gridLines)
    
    // Add axis label if it doesn't exist
    if (this.yAxisGroup.selectAll('.axis-label').empty()) {
      this.yAxisGroup.append('text')
        .attr('class', 'axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('y', -50)
        .attr('x', -this.actualInnerHeight / 2)
        .style('text-anchor', 'middle')
        .style('font-size', '12px')
        .text('Price')
    }
  }

  updateGridLines() {
    this.gridLinesGroup.selectAll('line')
      .data(this.yScale.ticks(5))
      .join('line')
      .attr('x1', 0)
      .attr('x2', this.actualInnerWidth)
      .attr('y1', d => this.yScale(d))
      .attr('y2', d => this.yScale(d))
      .style('stroke', this.colors.gridLines)
      .style('stroke-dasharray', '3,3')
      .style('opacity', 0.3)
  }

  updateCandlesticks(data: ProcessedCandleData[]) {
    const candleWidth = Math.max(1, (this.actualInnerWidth / data.length) * 0.7)
    
    // DATA JOIN
    const candles = this.candlesGroup.selectAll('.candle')
      .data(data, (d: any) => d.date.getTime()) // Key function for object constancy
    
    // EXIT
    candles.exit().remove()
    
    // ENTER
    const candlesEnter = candles.enter().append('g')
      .attr('class', 'candle')
    
    candlesEnter.append('line').attr('class', 'candle-line')
    candlesEnter.append('rect').attr('class', 'candle-body')
    
    // UPDATE + ENTER (merge)
    const candlesUpdate = candlesEnter.merge(candles as any)
    
    candlesUpdate
      .attr('transform', d => `translate(${this.xScale(d.date)},0)`)
    
    candlesUpdate.select('.candle-line')
      .attr('y1', d => this.yScale(d.high))
      .attr('y2', d => this.yScale(d.low))
      .attr('stroke', d => d.close >= d.open ? this.colors.upColor : this.colors.downColor)
    
    candlesUpdate.select('.candle-body')
      .attr('x', -candleWidth / 2)
      .attr('y', d => this.yScale(Math.max(d.open, d.close)))
      .attr('width', candleWidth)
      .attr('height', d => Math.max(1, Math.abs(this.yScale(d.open) - this.yScale(d.close))))
      .attr('fill', d => d.close >= d.open ? this.colors.upColor : this.colors.downColor)
  }

  updateVolume(data: ProcessedCandleData[]) {
    const barWidth = Math.max(1, (this.actualInnerWidth / data.length) * 0.7)
    
    const volumeBars = this.volumeGroup.selectAll('.volume-bar')
      .data(data, (d: any) => d.date.getTime())
      
    volumeBars.exit().remove()
    
    const volumeBarsEnter = volumeBars.enter().append('rect')
      .attr('class', 'volume-bar')
      
    volumeBarsEnter.merge(volumeBars as any)
      .attr('x', d => this.xScale(d.date) - barWidth / 2)
      .attr('y', d => this.volumeScale(d.volume))
      .attr('width', barWidth)
      .attr('height', d => this.volumeHeight - this.volumeScale(d.volume))
      .attr('fill', d => d.close >= d.open ? this.colors.upColor : this.colors.downColor)
      .attr('opacity', 0.3)
      
    this.volumeAxisGroup
      .call(d3.axisLeft(this.volumeScale).ticks(3).tickFormat(d => formatVolume(d as number)) as any)
      
    this.volumeAxisGroup.selectAll('line').style('stroke', this.colors.gridLines)
    this.volumeAxisGroup.select('.domain').style('stroke', this.colors.gridLines)

    // Add volume axis label if it doesn't exist
    if (this.volumeAxisGroup.selectAll('.volume-axis-label').empty()) {
      this.volumeAxisGroup.append('text')
        .attr('class', 'volume-axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('y', -50)
        .attr('x', -this.volumeHeight / 2)
        .style('text-anchor', 'middle')
        .style('font-size', '12px')
        .text('Volume')
    }
  }

  setupZoom() {
    if (!this.svg || this.isZoomInitialized) return

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 10])
      .translateExtent([[0, 0], [this.width, this.height]])
      .on('zoom', (event) => {
        this.currentTransform = event.transform
        this.handleZoom()
      })
    
    this.svg.call(zoom as any)
    this.isZoomInitialized = true
  }

  handleZoom() {
    const xScaleNew = this.currentTransform.rescaleX(this.xScale)
    
    // Update x-axis
    this.xAxisGroup
      .call(d3.axisBottom(xScaleNew).tickFormat(d => {
        const date = d as Date
        if (date.getHours() === 0 && date.getMinutes() === 0) return d3.timeFormat('%b %d')(date)
        return d3.timeFormat('%H:%M')(date)
      }) as any)
    
    // Update candle positions
    this.candlesGroup.selectAll('.candle')
      .attr('transform', (d: any) => `translate(${xScaleNew(d.date)},0)`)
    
    // Update volume bar positions
    if (this.showVolume) {
      const barWidth = Math.max(1, (this.actualInnerWidth / this.candlesGroup.selectAll('.candle').size()) * 0.7)
      this.volumeGroup.selectAll('.volume-bar')
        .attr('x', (d: any) => xScaleNew(d.date) - barWidth / 2)
    }
  }

  setupTooltipInteraction(data: ProcessedCandleData[]) {
    if (!this.overlayRect || !this.crosshairGroup || !this.tooltip) return

    this.overlayRect.on('mouseover', null).on('mouseout', null).on('mousemove', null)

    this.overlayRect
      .on('mouseover', () => {
        this.crosshairGroup.style('display', null)
        this.tooltip.style('opacity', 1)
      })
      .on('mouseout', () => {
        this.crosshairGroup.style('display', 'none')
        this.tooltip.style('opacity', 0)
        this.candlesGroup.selectAll('.candle').classed('selected', false).style('opacity', 1)
      })
      .on('mousemove', (event) => {
        const [mouseX, mouseY] = d3.pointer(event)
        const bisect = d3.bisector<ProcessedCandleData, Date>((d) => d.date).left
        const transformedXScale = this.currentTransform.rescaleX(this.xScale)
        const x0 = transformedXScale.invert(mouseX)
        const i = bisect(data, x0, 1)

        let selectedData: ProcessedCandleData
        let selectedIndex: number

        if (i === 0) {
          selectedIndex = 0
        } else if (i >= data.length) {
          selectedIndex = data.length - 1
        } else {
          const d0 = data[i - 1]
          const d1 = data[i]
          selectedIndex = x0.getTime() - d0.date.getTime() > d1.date.getTime() - x0.getTime() ? i : i - 1
        }
        selectedData = data[selectedIndex]

        if (selectedData) {
          const snappedX = transformedXScale(selectedData.date)

          this.crosshairGroup.select('.crosshair-x').attr('x1', snappedX).attr('x2', snappedX)
          this.crosshairGroup.select('.crosshair-y').attr('y1', mouseY).attr('y2', mouseY)

          this.candlesGroup.selectAll('.candle').style('opacity', 0.7)
          this.candlesGroup
            .selectAll('.candle')
            .filter((_d, idx) => idx === selectedIndex)
            .style('opacity', 1)

          this.updateTooltipContent(selectedData, event)
        }
      })
  }

  updateTooltipContent(d: ProcessedCandleData, event: MouseEvent) {
    const change = d.close - d.open
    const changePercent = (change / d.open) * 100
    const changeColor = change >= 0 ? this.colors.upColor : this.colors.downColor
    
    this.tooltip.html(`
      <div style="font-weight: bold; margin-bottom: 4px;">${d3.timeFormat('%Y-%m-%d %H:%M')(d.date)}</div>
      <div>Open: ${formatPrice(d.open)}</div>
      <div>High: ${formatPrice(d.high)}</div>
      <div>Low: ${formatPrice(d.low)}</div>
      <div>Close: ${formatPrice(d.close)}</div>
      <div style="color: ${changeColor}">Change: ${change >= 0 ? '+' : ''}${formatPrice(change)} (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%)</div>
      <div>Volume: ${formatVolume(d.volume)}</div>
    `)
    .style('left', `${event.pageX + 15}px`)
    .style('top', `${event.pageY - 15}px`)
  }
}
</script>

<style lang="scss" scoped>
.candlestick-chart-container {
  background: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface));
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  :deep(svg text) {
    fill: rgb(var(--v-theme-on-surface));
  }
}

.chart-header {
  margin-bottom: 16px;
}

.chart-title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
}

.chart-subtitle {
  font-size: 14px;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.7;
}

.chart-svg-container {
  width: 100%;
  overflow-x: auto;
  min-height: 400px; /* Ensure minimum height during loading */
  position: relative;
}

/* Global styles for tooltip */
:global(.candlestick-tooltip) {
  z-index: 1000;
}

:deep(.candle) {
  transition: opacity 0.2s ease-in-out;
}
</style>
